import { translate } from "@atoms/atom-client/i18n";
export enum AdditionalArticleUnit {
  Piece = "Piece",
  Meter = "Meter",
  SquareMeter = "SquareMeter",
  Liter = "Liter",
  Kilogram = "Kilogram",
  Set = "Set",
  Pair = "Pair",
  FlatRate = "FlatRate",
}

/** Unit labels in the UI language (read on access). */
export const ADDITIONAL_ARTICLE_UNIT_LABELS: Record<
  AdditionalArticleUnit,
  string
> = {
  get [AdditionalArticleUnit.Piece]() {
    return translate("app.shared.additionalArticleUnits.piece", "Stück");
  },
  get [AdditionalArticleUnit.Meter]() {
    return translate("app.shared.additionalArticleUnits.meter", "Meter");
  },
  get [AdditionalArticleUnit.SquareMeter]() {
    return translate("app.shared.additionalArticleUnits.squareMeter", "m²");
  },
  get [AdditionalArticleUnit.Liter]() {
    return translate("app.shared.additionalArticleUnits.liter", "Liter");
  },
  get [AdditionalArticleUnit.Kilogram]() {
    return translate("app.shared.additionalArticleUnits.kilogram", "kg");
  },
  get [AdditionalArticleUnit.Set]() {
    return translate("app.shared.additionalArticleUnits.set", "Set");
  },
  get [AdditionalArticleUnit.Pair]() {
    return translate("app.shared.additionalArticleUnits.pair", "Paar");
  },
  get [AdditionalArticleUnit.FlatRate]() {
    return translate("app.shared.additionalArticleUnits.flatRate", "Pauschal");
  },
};

/** Units whose quantity is naturally fractional (e.g. 1.54 m²). */
export const DECIMAL_ADDITIONAL_ARTICLE_UNITS: ReadonlySet<AdditionalArticleUnit> =
  new Set([
    AdditionalArticleUnit.Meter,
    AdditionalArticleUnit.SquareMeter,
    AdditionalArticleUnit.Liter,
    AdditionalArticleUnit.Kilogram,
  ]);

/** Decimal text after locale normalization (dot separator, no thousands grouping). */
const NORMALIZED_DECIMAL_PATTERN = /^(?:\d+\.\d+|\d+|\.\d+)$/;

/** Parse user-entered decimal text (supports German comma separator). */
export function parseLocalizedDecimalInput(raw: string): number {
  const trimmed = raw.trim().replaceAll(/\s/g, "");
  if (trimmed === "") return Number.NaN;

  const hasComma = trimmed.includes(",");
  const hasDot = trimmed.includes(".");
  let normalized: string;
  if (hasComma && hasDot) {
    const lastComma = trimmed.lastIndexOf(",");
    const lastDot = trimmed.lastIndexOf(".");
    normalized =
      lastComma > lastDot
        ? trimmed.replaceAll(".", "").replace(",", ".")
        : trimmed.replaceAll(",", "");
  } else if (hasComma) {
    normalized = trimmed.replace(",", ".");
  } else {
    normalized = trimmed;
  }

  if (!NORMALIZED_DECIMAL_PATTERN.test(normalized)) return Number.NaN;

  return Number.parseFloat(normalized);
}

/** Format a stored number for German decimal text inputs (dot → comma). */
export function formatLocalizedDecimalInput(value: number): string {
  return String(value).replace(".", ",");
}

/** Precision of measured quantities; matches the 0.01 input step and the price report. */
const DECIMAL_COUNT_STEP = 0.01;

/** Normalize quantity: 0.01 steps for measured units, integers for piece-like units. */
export function normalizeAdditionalArticleCount(
  value: unknown,
  unit?: AdditionalArticleUnit | null,
): number {
  const parsed =
    typeof value === "string"
      ? parseLocalizedDecimalInput(value)
      : Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return 1;
  if (unit != null && DECIMAL_ADDITIONAL_ARTICLE_UNITS.has(unit)) {
    // Round so the stored quantity is exactly what the report displays;
    // otherwise quantity × unit price visibly disagrees with the total.
    return Math.max(DECIMAL_COUNT_STEP, Math.round(parsed * 100) / 100);
  }
  return Math.max(1, Math.round(parsed));
}
